import type { WebSocketMessageLoggerWorkerFns } from './websocketMessageLogger.worker';

export type WebSocketLogEntry = {
  timestamp: number;
  messageType: 'sent' | 'received';
  payload: unknown;
};

export type WebSocketUrlLogs = {
  url: string;
  logs: WebSocketLogEntry[];
};

type WebSocketMessageLoggerWorkerConstructor = new () => WebSocketMessageLoggerWorkerFns;

const ComlinkWebSocketLogger = new ComlinkWorker<{
  WebSocketMessageLoggerWorker: WebSocketMessageLoggerWorkerConstructor;
}>(new URL('./websocketMessageLogger.worker', import.meta.url));

let workerClientPromise: Promise<WebSocketMessageLoggerWorkerFns> | null = null;
let workerClientDisabled = false;

function getWorkerClientPromise(): Promise<WebSocketMessageLoggerWorkerFns> | null {
  if (workerClientDisabled) {
    return null;
  }

  if (workerClientPromise) {
    return workerClientPromise;
  }

  try {
    workerClientPromise = Promise.resolve(new ComlinkWebSocketLogger.WebSocketMessageLoggerWorker());
  } catch {
    workerClientDisabled = true;
    workerClientPromise = null;
  }

  return workerClientPromise;
}

export function addWebSocketLog(url: string, messageType: 'sent' | 'received', payload: unknown): void {
  addWebSocketLogViaWorker(url, messageType, payload);
}

export function addWebSocketLogViaWorker(url: string, messageType: 'sent' | 'received', payload: unknown): void {
  const timestamp = Date.now();
  const loggerWorkerPromise = getWorkerClientPromise();

  if (!loggerWorkerPromise) {
    return;
  }

  void loggerWorkerPromise
    .then((loggerWorker) => loggerWorker.addLog(url, messageType, payload, timestamp))
    .catch(() => {
      workerClientDisabled = true;
    });
}

export async function getWebSocketLogs(): Promise<WebSocketUrlLogs[]> {
  const loggerWorkerPromise = getWorkerClientPromise();
  if (!loggerWorkerPromise) {
    return [];
  }

  try {
    const loggerWorker = await loggerWorkerPromise;
    return await loggerWorker.getLogs();
  } catch {
    workerClientDisabled = true;
    return [];
  }
}

export async function downloadWebSocketLogs(): Promise<boolean> {
  return downloadWebSocketLogsViaWorker();
}

export async function downloadWebSocketLogsViaWorker(): Promise<boolean> {
  const loggerWorkerPromise = getWorkerClientPromise();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'Prophecy Application Page';
  const pageStartTime = Date.now();
  let content: string | null = null;

  if (loggerWorkerPromise) {
    try {
      const loggerWorker = await loggerWorkerPromise;
      content = await loggerWorker.buildHarContent(pageUrl, pageStartTime);
    } catch {
      workerClientDisabled = true;
      content = null;
    }
  }

  if (!content) {
    return false;
  }

  const blob = new Blob([content], { type: 'application/json' });
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `websocket-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.har`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);

  return true;
}
